import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFavoritesPosts } from '../hooks/useFavoritesPosts';
import { useTranslation } from '../hooks/useTranslation';
import AuthModal from './AuthModal';
import MentionAutocomplete from './MentionAutocomplete';
import CommentPreview from './CommentPreview';
import { renderCommentText } from '../utils/commentUtils';
import styles from '../pages/PostsPage.module.css';

function PostCard({ post, currentUser }) {
  const navigate = useNavigate();
  const { currentUser: authUser } = useAuth();
  const { t } = useTranslation();
  const { isFavorite, toggleFavorite } = useFavoritesPosts();
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [localCommentsCount, setLocalCommentsCount] = useState(post.commentsCount || 0);
  const [commentFiles, setCommentFiles] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [commentPreviewUrls, setCommentPreviewUrls] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const commentInputRef = useRef(null);
  const mentionContainerRef = useRef(null);
  const [authorHairstylistId, setAuthorHairstylistId] = useState(null);

  useEffect(() => {
    const fetchComments = async () => {
      setLoadingComments(true);
      try {
        // Récupérer les commentaires sans orderBy pour éviter le besoin d'index composite
        const commentsQuery = query(
          collection(db, 'comments'),
          where('postId', '==', post.id)
        );
        const commentsSnapshot = await getDocs(commentsQuery);
        const commentsData = commentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).sort((a, b) => {
          // Trier côté client par date
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
          return aDate.getTime() - bDate.getTime();
        });
        setComments(commentsData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching comments:', error);
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
  }, [post.id]);

  useEffect(() => {
    const urls = commentFiles.map((file) => URL.createObjectURL(file));
    setCommentPreviewUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [commentFiles]);

  // Chercher le profil coiffeuse associé à l'auteur du post (si c'est une coiffeuse)
  useEffect(() => {
    let cancelled = false;

    const fetchHairstylistProfile = async () => {
      if (!post.userId) {
        setAuthorHairstylistId(null);
        return;
      }

      try {
        const q = query(
          collection(db, 'hairstylists'),
          where('userId', '==', post.userId),
        );
        const snapshot = await getDocs(q);
        if (!cancelled && !snapshot.empty) {
          setAuthorHairstylistId(snapshot.docs[0].id);
        } else if (!cancelled) {
          setAuthorHairstylistId(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching hairstylist for post author:', error);
        if (!cancelled) {
          setAuthorHairstylistId(null);
        }
      }
    };

    fetchHairstylistProfile();

    return () => {
      cancelled = true;
    };
  }, [post.userId]);

  const displayName =
    authUser?.displayName || authUser?.email?.split('@')[0] || '';
  const isAdminUser = authUser && displayName.toLowerCase() === 'admin';

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Quand la section commentaires est ouverte, on affiche tous les commentaires
  const displayedComments = comments;

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!authUser) {
      setShowAuthModal(true);
      return;
    }

    if (!commentText.trim() && commentFiles.length === 0) return;

    setSubmittingComment(true);
    const commentTextValue = commentText.trim();

    try {
      // Upload des images de commentaire si présentes
      let photoUrls = [];
      if (commentFiles.length > 0) {
        const uploadPromises = Array.from(commentFiles).map((file) => {
          if (!file.type.startsWith('image/')) return null;
          const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
          const storageRef = ref(
            storage,
            `commentPhotos/${post.id}/${authUser.uid}/${Date.now()}-${safeName}`,
          );
          return uploadBytes(storageRef, file).then((snapshot) =>
            getDownloadURL(snapshot.ref),
          );
        }).filter(Boolean);

        photoUrls = await Promise.all(uploadPromises);
      }

      const commentRef = await addDoc(collection(db, 'comments'), {
        postId: post.id,
        userId: authUser.uid,
        userName: authUser.displayName || authUser.email?.split('@')[0],
        text: commentTextValue,
        photos: photoUrls,
        createdAt: serverTimestamp(),
      });

      // Mettre à jour le compteur de commentaires
      const postRef = doc(db, 'posts', post.id);
      const postDoc = await getDoc(postRef);
      const currentCount = postDoc.data()?.commentsCount || 0;
      await updateDoc(postRef, {
        commentsCount: currentCount + 1,
      });

      // Ajouter le nouveau commentaire au state
      const commentDoc = await getDoc(commentRef);
      const realComment = {
        id: commentDoc.id,
        ...commentDoc.data(),
      };
      setComments((prev) => [...prev, realComment]);
      
      // Mettre à jour le compteur local immédiatement
      setLocalCommentsCount((prev) => prev + 1);
      
      setCommentText('');
      setCommentFiles([]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeletePost = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
      setDeleting(true);
      try {
        await deleteDoc(doc(db, 'posts', post.id));
        window.location.reload(); // Recharger pour actualiser la liste
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting post:', error);
        alert('Erreur lors de la suppression');
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleEditPost = () => {
    navigate(`/posts/edit/${post.id}`);
  };

  const handleFavoriteToggle = async () => {
    if (!authUser) {
      setShowAuthModal(true);
      return;
    }
    try {
      await toggleFavorite(post.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error toggling favorite:', error);
    }
  };

  const isAuthor = authUser && post.userId === authUser.uid;

  const handleOpenLightbox = (index, photos) => {
    const sourcePhotos = photos && photos.length > 0 ? photos : post.images;
    if (!sourcePhotos || sourcePhotos.length === 0) return;
    setLightboxPhotos(sourcePhotos);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handlePrevLightbox = (event) => {
    event.stopPropagation();
    if (!lightboxPhotos || lightboxPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev - 1 + lightboxPhotos.length) % lightboxPhotos.length);
  };

  const handleNextLightbox = (event) => {
    event.stopPropagation();
    if (!lightboxPhotos || lightboxPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev + 1) % lightboxPhotos.length);
  };

  const handleStartEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text || '');
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEditComment = async (comment) => {
    if (!authUser) {
      setShowAuthModal(true);
      return;
    }
    const canEdit =
      comment.userId === authUser.uid || isAdminUser;
    if (!canEdit) return;

    const newText = editingCommentText.trim();
    try {
      await updateDoc(doc(db, 'comments', comment.id), {
        text: newText,
      });
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id ? { ...c, text: newText } : c,
        ),
      );
      setEditingCommentId(null);
      setEditingCommentText('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!authUser) {
      setShowAuthModal(true);
      return;
    }
    const canDelete =
      comment.userId === authUser.uid || isAdminUser;
    if (!canDelete) return;

    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir supprimer ce commentaire ?',
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'comments', comment.id));
      setComments((prev) => prev.filter((c) => c.id !== comment.id));

      // Mettre à jour le compteur de commentaires
      const postRef = doc(db, 'posts', post.id);
      const postDoc = await getDoc(postRef);
      const currentCount = postDoc.data()?.commentsCount || 0;
      await updateDoc(postRef, {
        commentsCount: Math.max(0, currentCount - 1),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting comment:', error);
    }
  };

  // Récupérer les IDs de profil coiffeuse pour les auteurs de commentaires
  const [commentHairstylistMap, setCommentHairstylistMap] = useState({});

  useEffect(() => {
    let cancelled = false;

    const fetchHairstylistsForComments = async () => {
      try {
        const uniqueUserIds = Array.from(
          new Set(
            comments
              .map((c) => c.userId)
              .filter((id) => !!id),
          ),
        );

        if (!uniqueUserIds.length) {
          setCommentHairstylistMap({});
          return;
        }

        // Firestore limite les requêtes 'in' à 10 valeurs max
        const batch = uniqueUserIds.slice(0, 10);
        const q = query(
          collection(db, 'hairstylists'),
          where('userId', 'in', batch),
        );
        const snapshot = await getDocs(q);
        if (cancelled) return;

        const map = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.userId) {
            map[data.userId] = docSnap.id;
          }
        });
        setCommentHairstylistMap(map);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching hairstylists for comments:', error);
        if (!cancelled) {
          setCommentHairstylistMap({});
        }
      }
    };

    fetchHairstylistsForComments();

    return () => {
      cancelled = true;
    };
  }, [comments]);

  return (
    <>
      <div className={styles.postCard}>
        {/* Header du post - style Facebook */}
        <div className={styles.postHeader}>
          <div className={styles.postAuthor}>
            <button
              type="button"
              className={styles.authorAvatarButton}
              onClick={() => {
                if (authorHairstylistId) {
                  navigate(`/hairstylist/${authorHairstylistId}`);
                }
              }}
              disabled={!authorHairstylistId}
            >
              <div className={styles.authorAvatar}>
                {post.authorName?.[0] || 'U'}
              </div>
            </button>
            <div className={styles.postAuthorInfo}>
              {authorHairstylistId ? (
                <button
                  type="button"
                  className={styles.authorNameButton}
                  onClick={() => navigate(`/hairstylist/${authorHairstylistId}`)}
                >
                  {post.authorName || 'Utilisateur'}
                </button>
              ) : (
                <h3 className={styles.authorName}>{post.authorName || 'Utilisateur'}</h3>
              )}
              <div className={styles.postMeta}>
                <span className={styles.postDate}>{formatDate(post.createdAt)}</span>
                <span className={styles.postPrivacy}>🌐</span>
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button 
              type="button" 
              className={styles.favoriteButton}
              onClick={handleFavoriteToggle}
              aria-label={isFavorite(post.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              {isFavorite(post.id) ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#8e6acf" stroke="none">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              )}
            </button>
            {isAuthor && (
              <div className={styles.menuContainer}>
                <button 
                  type="button" 
                  className={styles.postOptions} 
                  aria-label="Options"
                  onClick={() => setShowMenu(!showMenu)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </button>
                {showMenu && (
                  <>
                    <div className={styles.menuOverlay} onClick={() => setShowMenu(false)} />
                    <div className={styles.menu}>
                      <button type="button" onClick={() => { handleEditPost(); setShowMenu(false); }}>
                        Modifier
                      </button>
                      <button 
                        type="button" 
                        onClick={() => { handleDeletePost(); setShowMenu(false); }}
                        disabled={deleting}
                        className={styles.deleteOption}
                      >
                        {deleting ? 'Suppression...' : 'Supprimer'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Contenu du post */}
        <div className={styles.postContent}>
          {post.text && <p className={styles.postText}>{post.text}</p>}
          {post.images && post.images.length > 0 && (
            <div className={styles.postImages}>
              {post.images.map((img, idx) => (
                <button
                  key={img}
                  type="button"
                  className={styles.postImageWrapper}
                  onClick={() => handleOpenLightbox(idx, post.images)}
                >
                  <img src={img} alt={`Post ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions et compteurs - style Facebook */}
        <div className={styles.postActions}>
          <div className={styles.postReactions}>
            <span 
              className={styles.reactionCount}
              onClick={() => setShowComments(!showComments)}
              style={{ cursor: 'pointer' }}
            >
              {(() => {
                const count = localCommentsCount || comments.length || 0;
                return count === 1 ? '1 commentaire' : `${count} commentaires`;
              })()}
            </span>
          </div>
        </div>

        {/* Commentaires - style Facebook */}
        {comments.length > 0 && (
          <div className={`${styles.commentsSection} ${showComments ? styles.commentsExpanded : ''}`}>
            {loadingComments ? (
              <div className={styles.loadingComments}>{t('common.loading')}</div>
            ) : (
              <div className={styles.commentsList}>
                {displayedComments.map((comment) => (
                  <div key={comment.id} className={styles.comment}>
                    <div className={styles.commentAvatar}>
                      {comment.userName?.[0] || 'U'}
                    </div>
                    <div className={styles.commentContent}>
                        <div className={styles.commentBubble}>
                          {commentHairstylistMap[comment.userId] ? (
                            <button
                              type="button"
                              className={styles.commentAuthorHairstylist}
                              onClick={() => navigate(`/hairstylist/${commentHairstylistMap[comment.userId]}`)}
                            >
                              {comment.userName || 'Utilisateur'}
                            </button>
                          ) : (
                            <strong className={styles.commentAuthor}>{comment.userName || 'Utilisateur'}</strong>
                          )}
                          {editingCommentId === comment.id ? (
                            <input
                              type="text"
                              className={styles.commentEditInput}
                              value={editingCommentText}
                              onChange={(e) =>
                                setEditingCommentText(e.target.value)
                              }
                            />
                          ) : (
                            <p className={styles.commentText}>
                              {renderCommentText(comment.text || '').map((part, idx) => {
                                if (part.type === 'link') {
                                  return (
                                    <a
                                      key={idx}
                                      href={part.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={styles.commentLink}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {part.content}
                                    </a>
                                  );
                                }
                                if (part.type === 'mention') {
                                  return (
                                    <Link
                                      key={idx}
                                      to={`/hairstylist/${part.id}`}
                                      className={styles.commentMention}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      @{part.name}
                                    </Link>
                                  );
                                }
                                return <span key={idx}>{part.content}</span>;
                              })}
                            </p>
                          )}
                        {comment.photos && comment.photos.length > 0 && (
                          <div className={styles.commentPhotos}>
                            {comment.photos.map((url, index) => (
                              <button
                                key={url}
                                type="button"
                                className={styles.commentPhotoWrapper}
                                onClick={() => handleOpenLightbox(index, comment.photos)}
                              >
                                <img src={url} alt={`Photo ${index + 1} du commentaire`} />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                        <div className={styles.commentActions}>
                          <span className={styles.commentDate}>{formatDate(comment.createdAt)}</span>
                          {authUser && (comment.userId === authUser.uid || isAdminUser) && (
                            <div className={styles.commentActionButtons}>
                              {editingCommentId === comment.id ? (
                                <>
                                  <button
                                    type="button"
                                    className={styles.commentAction}
                                    onClick={() => handleSaveEditComment(comment)}
                                  >
                                    Enregistrer
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.commentAction}
                                    onClick={handleCancelEditComment}
                                  >
                                    {t('common.cancel')}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className={styles.commentAction}
                                    onClick={() => handleStartEditComment(comment)}
                                  >
                                    Modifier
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.commentAction}
                                    onClick={() => handleDeleteComment(comment)}
                                  >
                                    Supprimer
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Formulaire de commentaire - style Facebook */}
        {currentUser ? (
          <form className={styles.commentForm} onSubmit={handleCommentSubmit}>
            <div className={styles.commentFormAvatar}>
              {authUser?.displayName?.[0] || authUser?.email?.[0] || 'U'}
            </div>
            <div className={styles.commentFormInput} ref={mentionContainerRef}>
              <div className={styles.commentFormInputRow}>
                <input
                  ref={commentInputRef}
                  type="text"
                  placeholder="Écrire un commentaire..."
                  value={commentText}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setCommentText(newValue);
                    
                    // Vérifier si on doit afficher l'autocomplétion
                    const mentionMatch = newValue.match(/@(\w{3,})$/);
                    if (mentionMatch && commentInputRef.current) {
                      const rect = commentInputRef.current.getBoundingClientRect();
                      const containerRect = mentionContainerRef.current?.getBoundingClientRect();
                      setMentionPosition({
                        top: rect.bottom - (containerRect?.top || 0) + 5,
                        left: rect.left - (containerRect?.left || 0),
                      });
                      setShowMentionAutocomplete(true);
                    } else {
                      setShowMentionAutocomplete(false);
                    }
                  }}
                  className={styles.commentInput}
                />
                {showMentionAutocomplete && (
                  <MentionAutocomplete
                    value={commentText}
                    onChange={setCommentText}
                    position={mentionPosition}
                    inputRef={commentInputRef}
                    onSelect={() => setShowMentionAutocomplete(false)}
                  />
                )}
                <div className={styles.commentPhotoInputRow}>
                <input
                  id={`comment-photos-${post.id}`}
                  type="file"
                  accept="image/*"
                  multiple
                  className={styles.commentFileInputHidden}
                  onChange={(e) => {
                    const selected = Array.from(e.target.files || []);
                    if (selected.length === 0) return;
                    setCommentFiles((prev) => {
                      const existingKeys = new Set(
                        prev.map((f) => `${f.name}_${f.lastModified}`),
                      );
                      const merged = [
                        ...prev,
                        ...selected.filter(
                          (file) =>
                            file.type.startsWith('image/') &&
                            !existingKeys.has(`${file.name}_${file.lastModified}`),
                        ),
                      ];
                      return merged.slice(0, 3);
                    });
                  }}
                />
                <label
                  htmlFor={`comment-photos-${post.id}`}
                  className={styles.commentPhotoIconButton}
                  aria-label="Ajouter des photos"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 7H7L9 4H15L17 7H20C21.1046 7 22 7.89543 22 9V19C22 20.1046 21.1046 21 20 21H4C2.89543 21 2 20.1046 2 19V9C2 7.89543 2.89543 7 4 7Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="13"
                      r="3.5"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </label>
              </div>
              </div>
              {commentText && (
                <CommentPreview text={commentText} />
              )}
              {commentPreviewUrls.length > 0 && (
                <div className={styles.commentPreviewRow}>
                  {commentPreviewUrls.map((url, index) => (
                    <div key={url} className={styles.commentPreview}>
                      <img src={url} alt={`Prévisualisation ${index + 1}`} />
                    </div>
                  ))}
                </div>
              )}
              <button
                type="submit"
                className={styles.commentSubmitBtn}
                disabled={submittingComment || (!commentText.trim() && commentFiles.length === 0)}
              >
                {submittingComment ? '...' : 'Publier'}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.loginPrompt}>
            <button
              type="button"
              className={styles.loginToCommentBtn}
              onClick={() => setShowAuthModal(true)}
            >
              Connecte-toi pour commenter
            </button>
          </div>
        )}
      </div>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {lightboxOpen && lightboxPhotos && lightboxPhotos.length > 0 && (
        <div
          className={styles.lightboxOverlay}
          role="dialog"
          aria-modal="true"
          onClick={handleCloseLightbox}
        >
          <div
            className={styles.lightboxContent}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={handleCloseLightbox}
              aria-label={t('common.close')}
            >
              ×
            </button>
            {lightboxPhotos.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                  onClick={handlePrevLightbox}
                  aria-label="Photo précédente"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                  onClick={handleNextLightbox}
                  aria-label="Photo suivante"
                >
                  ›
                </button>
              </>
            )}
            <img
              src={lightboxPhotos[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1} sur ${lightboxPhotos.length}`}
              className={styles.lightboxImage}
            />
            {lightboxPhotos.length > 1 && (
              <div className={styles.lightboxCounter}>
                {lightboxIndex + 1} / {lightboxPhotos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default PostCard;

